import React, { useState, useEffect, useContext, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { X, Plus, Minus, Users, Package, ChevronDown, Search } from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import { UserContext } from '../../contexts/UserContext';
import MultiSelectDropdown from '../common/MultiSelectDropdown';

const GroupModal = ({
  isOpen,
  onClose,
  mode = 'create', // 'create', 'edit', 'view'
  groupData = null,
  equipmentData = {},
  preSelectedEquipment = [],
  onSave,
  // Group actions callbacks
  onInspectGroup = null,
  onAssignGroup = null
}) => {
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(false);

  // Equipment data states
  const [allEquipment, setAllEquipment] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [models, setModels] = useState([]);
  const [hasMoreEquipment, setHasMoreEquipment] = useState(false);
  
  // Panel states
  const [showEquipmentPanel, setShowEquipmentPanel] = useState(false);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  
  // Equipment selection states
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [originalEquipmentIds, setOriginalEquipmentIds] = useState([]); // Track original equipment IDs when modal opens
  const [equipmentFilter, setEquipmentFilter] = useState('');
  const [showEquipmentDropdown, setShowEquipmentDropdown] = useState(false);
  
  // Smart filtering states
  const [smartFiltering, setSmartFiltering] = useState(false);
  const [requiredStatus, setRequiredStatus] = useState('');
  const [requiredAssignedUser, setRequiredAssignedUser] = useState(null);
  
  // Filter states - using arrays for multi-select
  const [selectedTypeFilter, setSelectedTypeFilter] = useState([]);
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState([]);
  const [selectedModelFilter, setSelectedModelFilter] = useState([]);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState([]);
  
  // Form data for group details
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: '',
    location: '',
    notes: '',
    nfc_tag_number: ''
  });

  const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;
  const isEditing = mode === 'edit' && groupData;

  // Track if modal was just opened to know when to reset state
  const wasOpenRef = React.useRef(false);

  // Track if we made changes in view mode that need parent refresh
  const needsParentRefreshRef = React.useRef(false);

  // Track if this is the first search render (to avoid fetching on initial load)
  const isFirstSearchRenderRef = useRef(true);

  // Track previous group ID to detect group changes
  const previousGroupIdRef = useRef(null);

  // Local copy of groupData that we can update after operations
  const [localGroupData, setLocalGroupData] = useState(groupData);

  // Sync localGroupData when groupData prop changes
  useEffect(() => {
    setLocalGroupData(groupData);
  }, [groupData]);

  // Wrapper for onClose that notifies parent if needed
  const handleClose = () => {
    if (mode === 'view' && needsParentRefreshRef.current && onSave) {
      console.log('Modal closing - notifying parent to refresh');
      onSave();
      needsParentRefreshRef.current = false;
    }

    // Reset all state when closing modal to ensure clean state for next open
    console.log('🔄 Resetting all modal state on close');
    setAllEquipment([]);
    setSelectedEquipment([]);
    setOriginalEquipmentIds([]); // Reset original equipment IDs
    setSmartFiltering(false);
    setRequiredStatus('');
    setRequiredAssignedUser(null);
    setEquipmentFilter('');
    setShowEquipmentPanel(false);
    setShowFiltersPanel(false);
    setShowEquipmentDropdown(false);
    setSelectedTypeFilter([]);
    setSelectedCompanyFilter([]);
    setSelectedModelFilter([]);
    setSelectedStatusFilter([]);
    hasSetupFiltersRef.current = false;
    isFirstSearchRenderRef.current = true;
    previousGroupIdRef.current = null;

    onClose();
  };

  // Debug: Log when selectedEquipment changes
  useEffect(() => {
    console.log('selectedEquipment changed:', selectedEquipment);
    console.trace('selectedEquipment change stack trace');
  }, [selectedEquipment]);

  // Initialize form data when modal is opened
  // State reset is handled in handleClose() for simplicity
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;

    if (justOpened) {
      console.log('🔄 Modal opened -', groupData?.name || 'new group');

      if (mode === 'edit' || mode === 'view') {
        setFormData({
          name: groupData.name || '',
          description: groupData.description || '',
          status: groupData.status || '',
          location: groupData.location || '',
          notes: groupData.notes || '',
          nfc_tag_number: groupData.nfc_tag_number || ''
        });

        // Load existing equipment in group
        if (groupData.equipment_items) {
          const preSelectedIds = groupData.equipment_items.map(item => item.id);
          setSelectedEquipment(preSelectedIds);
          setOriginalEquipmentIds(preSelectedIds); // Save as original baseline for change detection
          // Auto-show equipment panel if there are existing items or in view mode
          if (groupData.equipment_items.length > 0 || mode === 'view') {
            setShowEquipmentPanel(true);
            // Load full data for equipment in group first
            // fetchEquipment will be called by useEffect after smart filtering is set up
            refreshGroupData();
          }
        } else if (mode === 'view') {
          // Even if no items, show panel in view mode
          setShowEquipmentPanel(true);
          // fetchEquipment will be called by useEffect
        }
      } else {
        // Reset for create mode
        setFormData({
          name: '',
          description: '',
          status: '',
          location: '',
          notes: '',
          nfc_tag_number: ''
        });

        // Set pre-selected equipment if provided
        if (preSelectedEquipment && preSelectedEquipment.length > 0) {
          const preSelectedIds = preSelectedEquipment.map(item => item.id);
          setSelectedEquipment(preSelectedIds);
          setOriginalEquipmentIds(preSelectedIds); // Save as original baseline for change detection

          // Initialize allEquipment with pre-selected items so autoDetectGroupStatus can run
          setAllEquipment(preSelectedEquipment);

          // Auto-show equipment panel if there are pre-selected items
          setShowEquipmentPanel(true);
          // fetchEquipment will be called by useEffect after smart filtering is set up
        } else {
          setSelectedEquipment([]);
          setOriginalEquipmentIds([]); // No original equipment in create mode without pre-selected
          // Reset smart filtering
          setSmartFiltering(false);
          setRequiredStatus('');
          setRequiredAssignedUser(null);
        }
      }
    }
  }, [isOpen, mode, groupData, isEditing, preSelectedEquipment]);

  // Initialize smart filtering after equipment data is loaded
  useEffect(() => {
    if (allEquipment.length > 0 && selectedEquipment.length > 0) {
      // Auto-detect group status and enable smart filtering
      autoDetectGroupStatus(selectedEquipment);
    }
  }, [allEquipment]);

  // Fetch equipment when smart filtering is ready (in view or create mode)
  // This ensures filters are applied to backend request
  const hasSetupFiltersRef = React.useRef(false);
  useEffect(() => {
    if ((mode === 'view' || mode === 'create') && showEquipmentPanel && !hasSetupFiltersRef.current) {
      // WAIT for allEquipment to be loaded by refreshGroupData (view) or initialized (create)
      // (unless it's truly an empty group with no items at all)
      const isGroupEmpty = selectedEquipment.length === 0;

      if (isGroupEmpty) {
        // Truly empty group - fetch immediately without filters
        console.log('✅ Empty group (no selected items), fetching all equipment');
        hasSetupFiltersRef.current = true;
        fetchEquipment();
        return;
      }

      // For non-empty groups, wait for allEquipment to be populated
      if (allEquipment.length === 0) {
        console.log('⏳ Waiting for allEquipment to be populated...');
        return;
      }

      // Wait for smart filtering to be set up
      if (requiredStatus) {
        console.log('✅ Smart filtering is ready, fetching equipment with filters:', {
          mode,
          requiredStatus,
          requiredAssignedUser: requiredAssignedUser || 'none'
        });
        hasSetupFiltersRef.current = true;
        fetchEquipment();
      } else {
        console.log('⏳ Waiting for smart filtering to be set up (requiredStatus not ready)...');
      }
    }
  }, [mode, showEquipmentPanel, allEquipment.length, selectedEquipment.length, requiredStatus, requiredAssignedUser]);

  // Handle status filter changes - adjust smart filtering accordingly
  useEffect(() => {
    // Only react to status filter changes if we have selected equipment
    if (selectedEquipment.length > 0 && selectedStatusFilter.length > 0) {
      const selectedEquipmentObjects = allEquipment.filter(eq => selectedEquipment.includes(eq.id));

      // If status filter includes 'issued' or 'received' and we have issued/received items selected
      if (selectedStatusFilter.includes('issued') || selectedStatusFilter.includes('received')) {
        const firstIssuedOrReceivedItem = selectedEquipmentObjects.find(eq =>
          eq.safety_equipment_status === 'issued' || eq.safety_equipment_status === 'received'
        );
        if (firstIssuedOrReceivedItem) {
          setSmartFiltering(true);
          setRequiredStatus('issued_or_received');
          setRequiredAssignedUser(firstIssuedOrReceivedItem.assigned_to);
        }
      }
      // If status filter includes only 'available' and we have available items selected
      else if (selectedStatusFilter.includes('available') && !selectedStatusFilter.includes('issued') && !selectedStatusFilter.includes('received')) {
        const hasAvailableItems = selectedEquipmentObjects.some(eq => eq.safety_equipment_status === 'available');
        if (hasAvailableItems) {
          setSmartFiltering(true);
          setRequiredStatus('available');
          setRequiredAssignedUser(null);
        }
      }
    }
    // If no status filters are set, rely on auto-detection
    else if (selectedStatusFilter.length === 0 && selectedEquipment.length > 0) {
      autoDetectGroupStatus(selectedEquipment);
    }
  }, [selectedStatusFilter, selectedEquipment, allEquipment]);

  // Debounced search effect
  useEffect(() => {
    console.log('=== Search useEffect triggered ===');
    console.log('showEquipmentPanel:', showEquipmentPanel);
    console.log('equipmentFilter:', equipmentFilter);
    console.log('isFirstSearchRender:', isFirstSearchRenderRef.current);

    // Only trigger search if we have equipment panel open
    if (!showEquipmentPanel) {
      console.log('Equipment panel not open, skipping fetch');
      isFirstSearchRenderRef.current = true; // Reset for next open
      return;
    }

    // Skip only on the very first render (initial load handled by separate useEffect)
    if (isFirstSearchRenderRef.current) {
      console.log('First render, skipping (initial load handled by separate useEffect)');
      isFirstSearchRenderRef.current = false;
      return;
    }

    console.log('Setting up 1 second debounce...');
    const delayDebounceFn = setTimeout(() => {
      console.log('Debounce timeout fired! Calling fetchEquipment with:', equipmentFilter);
      // Fetch equipment with search query (can be empty to reload full list)
      fetchEquipment(equipmentFilter);
    }, 1000); // 1 second debounce - gives user time to type

    return () => {
      console.log('Clearing debounce timeout');
      clearTimeout(delayDebounceFn);
    };
  }, [equipmentFilter, showEquipmentPanel]);

  // Refresh group data from backend
  const refreshGroupData = async () => {
    console.log('=== refreshGroupData START ===');
    console.log('groupData:', groupData);
    console.log('groupData.id:', groupData?.id);

    if (!groupData || !groupData.id) {
      console.log('No groupData or groupData.id, returning early');
      return;
    }

    // Reset filters flag so fetchEquipment will be called after group data is loaded
    hasSetupFiltersRef.current = false;

    try {
      const token = localStorage.getItem("access_token");
      console.log('Fetching group data from backend...');
      const response = await axios.get(`${API_BASE_URL}/safetyequipmentgroup/${groupData.id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const freshGroupData = response.data;
      console.log('Fresh group data received:', freshGroupData);
      console.log('Fresh equipment_items:', freshGroupData.equipment_items);
      console.log('Number of items:', freshGroupData.equipment_items?.length || 0);

      // Update local copy of groupData
      setLocalGroupData(freshGroupData);
      console.log('Updated localGroupData with fresh data');

      // Update form data
      setFormData({
        name: freshGroupData.name || '',
        description: freshGroupData.description || '',
        status: freshGroupData.status || '',
        location: freshGroupData.location || '',
        notes: freshGroupData.notes || '',
        nfc_tag_number: freshGroupData.nfc_tag_number || ''
      });

      // Update selected equipment and fetch full data for items in the group
      if (freshGroupData.equipment_items && freshGroupData.equipment_items.length > 0) {
        const freshEquipmentIds = freshGroupData.equipment_items.map(item => item.id);
        console.log('Fresh equipment IDs:', freshEquipmentIds);

        // Fetch full data for each equipment item in the group
        console.log('Fetching full data for equipment items...');
        const equipmentPromises = freshEquipmentIds.map(id =>
          axios.get(`${API_BASE_URL}/safetyequipment/${id}/`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(error => {
            console.error(`Failed to fetch equipment ${id}:`, error);
            return null;
          })
        );

        const equipmentResponses = await Promise.all(equipmentPromises);
        const fullEquipmentData = equipmentResponses
          .filter(response => response !== null)
          .map(response => response.data);

        console.log('Fetched full equipment data for group:', fullEquipmentData);
        console.log('Full equipment data count:', fullEquipmentData.length);
        console.log('Full equipment IDs:', fullEquipmentData.map(e => e.id));
        console.log('Full equipment details:', fullEquipmentData.map(e => ({
          id: e.id,
          status: e.safety_equipment_status,
          assigned_to: e.assigned_to,
          assigned_to_name: e.assigned_to_name,
          equipment_group_id: e.equipment_group_id
        })));

        // Update allEquipment to include/update these items
        setAllEquipment(prevEquipment => {
          // Create a map of existing equipment for quick lookup
          const equipmentMap = new Map(prevEquipment.map(e => [e.id, e]));

          // Update or add fresh equipment data
          fullEquipmentData.forEach(freshItem => {
            equipmentMap.set(freshItem.id, freshItem);
          });

          const result = Array.from(equipmentMap.values());
          console.log('Updated allEquipment, now has', result.length, 'items');
          return result;
        });

        // Update selected equipment IDs
        console.log('Setting selectedEquipment to:', freshEquipmentIds);
        setSelectedEquipment(freshEquipmentIds);
        setOriginalEquipmentIds(freshEquipmentIds); // Save as original baseline for change detection

        // Auto-detect group status to set requiredStatus and requiredAssignedUser
        // This must happen BEFORE fetchEquipment is called so filters are applied
        console.log('Auto-detecting group status from fresh data...');
        const freshEquipmentObjects = fullEquipmentData;
        const statuses = freshEquipmentObjects.map(eq => eq.safety_equipment_status);
        const assignedUsers = freshEquipmentObjects.map(eq => eq.assigned_to).filter(user => user !== null && user !== undefined);

        // Normalize issued/received to 'issued' for grouping purposes
        const normalizedStatuses = statuses.map(s => (s === 'received' ? 'issued' : s));
        const uniqueStatuses = [...new Set(normalizedStatuses)];

        if (uniqueStatuses.length === 1 && uniqueStatuses[0] === 'issued') {
          // All items are issued/received - enable smart filtering
          const uniqueUsers = [...new Set(assignedUsers)];
          if (uniqueUsers.length === 1) {
            console.log('Setting smart filtering: issued_or_received with user', uniqueUsers[0]);
            setSmartFiltering(true);
            setRequiredStatus('issued_or_received');
            setRequiredAssignedUser(uniqueUsers[0]);
          }
        }
      } else {
        console.log('No equipment items in fresh data, clearing selection');
        setSelectedEquipment([]);
      }

      console.log('=== refreshGroupData END - SUCCESS ===');
    } catch (error) {
      console.error("=== refreshGroupData END - ERROR ===");
      console.error("Error refreshing group data:", error);
      toast.error("Failed to refresh group data");
    }
  };

  const fetchEquipment = async (searchQuery = '') => {
    console.log('🔍 fetchEquipment called with:', {
      searchQuery,
      mode,
      requiredStatus,
      requiredAssignedUser,
      selectedEquipmentCount: selectedEquipment.length
    });

    try {
      const token = localStorage.getItem("access_token");

      // Build query parameters
      const params = new URLSearchParams();
      params.append('page_size', '100'); // Limit to 100 results

      // Add search parameter if query is provided
      if (searchQuery && searchQuery.trim().length > 0) {
        params.append('search', searchQuery.trim());
      }

      // In view and create modes with smart filtering enabled, add backend filters
      // This helps show only compatible equipment that can be added to the group
      // Note: Backend table-data endpoint uses 'user' parameter (not 'assigned_user')
      // IMPORTANT: Apply these filters even when searching - we can only add compatible items!
      if ((mode === 'view' || mode === 'create') && requiredStatus) {
        console.log('✅ Adding backend filters:', {
          mode,
          requiredStatus,
          requiredAssignedUser,
          search: searchQuery || 'none'
        });

        // Add status filter based on group type
        if (requiredStatus === 'issued_or_received') {
          // issued and received are compatible statuses - we filter for both
          params.append('status', 'issued,received');
          if (requiredAssignedUser) {
            params.append('user', requiredAssignedUser);
          }
        } else {
          // For other statuses (available, service, damaged, out_of_date), filter by exact status
          params.append('status', requiredStatus);
        }
      } else {
        console.log('❌ NOT adding backend filters because:', {
          'mode': mode,
          'requiredStatus exists': !!requiredStatus
        });
      }

      // Note: type, company, model, status filters are applied client-side
      // in getSortedAndFilteredEquipment() function

      // Use optimized table-data endpoint (same as EquipmentManagementNoGroup)
      // This endpoint uses cached assignment fields and supports assigned_user filter
      const response = await axios.get(`${API_BASE_URL}/safetyequipment/table-data/?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("Equipment data fetched:", response.data);
      // Handle paginated response (response.data.results) or direct array
      let equipmentData = response.data?.results || response.data || [];

      // Normalize table-data structure to match old serializer format
      // table-data returns: { assignment: { user_id, user_name } }
      // We need: { assigned_to, assigned_to_name }
      equipmentData = equipmentData.map(eq => ({
        ...eq,
        assigned_to: eq.assignment?.user_id || null,
        assigned_to_name: eq.assignment?.user_name || null
      }));

      console.log('Equipment data count:', equipmentData.length);
      console.log('Equipment IDs from backend:', equipmentData.map(e => e.id));

      // Filter issued equipment for Karol for debugging
      const issuedForKarol = equipmentData.filter(e =>
        e.safety_equipment_status === 'issued' &&
        (e.assigned_to_name?.includes('Karol') || e.assigned_to_name?.includes('karol'))
      );
      console.log('🔍 Issued equipment for Karol from backend:', issuedForKarol.length);
      console.log('🔍 Issued for Karol IDs:', issuedForKarol.map(e => e.id));
      console.log('🔍 Issued for Karol details:', issuedForKarol.map(e => ({
        id: e.id,
        assigned_to: e.assigned_to,
        assigned_to_name: e.assigned_to_name,
        equipment_group_id: e.equipment_group_id
      })));

      // When searching/filtering, REPLACE available equipment BUT keep group equipment
      // Group equipment (selectedEquipment) should never disappear from allEquipment
      setAllEquipment(prevAll => {
        if (searchQuery && searchQuery.trim().length > 0) {
          // Search active - keep ONLY group equipment + search results
          console.log('🔍 Search active - keeping group equipment + search results');
          const groupEquipment = prevAll.filter(eq => selectedEquipment.includes(eq.id));

          // Combine group equipment with search results (using Map to avoid duplicates)
          const equipmentMap = new Map([...groupEquipment, ...equipmentData].map(eq => [eq.id, eq]));
          const result = Array.from(equipmentMap.values());

          console.log('Search result:', {
            groupEquipmentCount: groupEquipment.length,
            searchResultsCount: equipmentData.length,
            totalCount: result.length
          });

          return result;
        } else {
          // No search - merge normally (initial load or filter change)
          const existingEquipmentMap = new Map(prevAll.map(eq => [eq.id, eq]));

          console.log('Before merge - existing equipment IDs:', Array.from(existingEquipmentMap.keys()));

          // Add or update equipment from fetch results
          equipmentData.forEach(newEq => {
            existingEquipmentMap.set(newEq.id, newEq);
          });

          const result = Array.from(existingEquipmentMap.values());

          console.log('Merging equipment:', {
            existingCount: prevAll.length,
            newFetchedCount: equipmentData.length,
            totalCount: result.length
          });

          return result;
        }
      });

      // Check if there are more results
      const hasNext = response.data?.next !== null && response.data?.next !== undefined;
      setHasMoreEquipment(hasNext);
      if (hasNext) {
        console.log('More equipment available - total:', response.data?.count);
      }

      // Extract unique data for filters (only on initial load without search)
      if (!searchQuery) {
        const types = equipmentData?.reduce((acc, equipment) => {
          if (equipment.safety_equipment_type_name && !acc.find(type => type.name === equipment.safety_equipment_type_name)) {
            acc.push({
              id: equipment.safety_equipment_type,
              name: equipment.safety_equipment_type_name
            });
          }
          return acc;
        }, []) || [];

        const companiesData = equipmentData?.reduce((acc, equipment) => {
          if (equipment.company_name && !acc.find(company => company.name === equipment.company_name)) {
            acc.push({
              id: equipment.company,
              name: equipment.company_name
            });
          }
          return acc;
        }, []) || [];

        const modelsData = equipmentData?.reduce((acc, equipment) => {
          const modelName = getModelNameFromEquipment(equipment);
          if (modelName && modelName !== '-' && !acc.find(model => model.name === modelName)) {
            acc.push({
              id: equipment.safety_equipment_model,
              name: modelName
            });
          }
          return acc;
        }, []) || [];

        setEquipmentTypes(types);
        setCompanies(companiesData);
        setModels(modelsData);
      }
    } catch (error) {
      console.error("Error fetching equipment:", error);
    }
  };

  const getModelNameFromEquipment = (equipment) => {
    // This should match the logic from EquipmentManagement
    return equipment.safety_equipment_model_name || '-';
  };

  const getBrandFromEquipment = (equipment) => {
    // This should match the logic from EquipmentManagement
    return equipment.safety_equipment_model_brand || '-';
  };

  // Get user name by ID - uses assigned_to_name from equipment data instead of separate users list
  const getUserNameById = (userId) => {
    if (!userId) return 'Unknown User';
    // Find equipment assigned to this user and get the name from assigned_to_name
    const selectedEquipmentObjects = allEquipment.filter(eq => selectedEquipment.includes(eq.id));
    const equipment = selectedEquipmentObjects.find(eq => eq.assigned_to === userId);
    return equipment?.assigned_to_name || 'Unknown User';
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // When user manually changes status, update smart filtering
    if (field === 'status') {
      updateSmartFiltering(value);
    }
  };

  // Update smart filtering based on group status
  const updateSmartFiltering = (groupStatus) => {
    console.log('🔧 updateSmartFiltering called with:', groupStatus);

    if (!groupStatus) {
      console.log('No groupStatus, disabling filtering');
      setSmartFiltering(false);
      setRequiredStatus('');
      setRequiredAssignedUser(null);
      return;
    }

    if (groupStatus === 'available') {
      console.log('Setting filters for available status');
      setSmartFiltering(true);
      setRequiredStatus('available');
      setRequiredAssignedUser(null);
    } else if (groupStatus === 'issued' || groupStatus === 'received') {
      console.log('Setting filters for issued/received status');
      setSmartFiltering(true);
      // For issued/received groups, accept both statuses (they're compatible)
      setRequiredStatus('issued_or_received');
      // If we have selected equipment, use the assigned user from the first issued/received item
      const selectedEquipmentObjects = allEquipment.filter(equipment => selectedEquipment.includes(equipment.id));
      const firstIssuedOrReceivedItem = selectedEquipmentObjects.find(eq =>
        eq.safety_equipment_status === 'issued' || eq.safety_equipment_status === 'received'
      );
      setRequiredAssignedUser(firstIssuedOrReceivedItem?.assigned_to || null);
    } else if (groupStatus === 'service' || groupStatus === 'damaged' || groupStatus === 'out_of_date') {
      // For service/damaged/out_of_date groups, filter by that specific status
      console.log('Setting filters for service/damaged/out_of_date status:', groupStatus);
      setSmartFiltering(true);
      setRequiredStatus(groupStatus);
      setRequiredAssignedUser(null);
    } else {
      // Unknown status - disable filtering
      console.warn('Unknown status, disabling filtering:', groupStatus);
      setSmartFiltering(false);
      setRequiredStatus('');
      setRequiredAssignedUser(null);
    }
  };

  // Auto-detect and set group status based on selected equipment
  const autoDetectGroupStatus = (selectedEquipmentIds) => {
    console.log('🔍 autoDetectGroupStatus called with IDs:', selectedEquipmentIds);

    if (selectedEquipmentIds.length === 0) {
      // No equipment selected, keep status empty
      console.log('No equipment selected, clearing status');
      setFormData(prev => ({ ...prev, status: '' }));
      setSmartFiltering(false);
      setRequiredStatus('');
      setRequiredAssignedUser(null);
      return;
    }

    const selectedEquipmentObjects = allEquipment.filter(equipment => selectedEquipmentIds.includes(equipment.id));
    console.log('Selected equipment objects:', selectedEquipmentObjects.length);

    // Don't proceed if we can't find the equipment in allEquipment yet
    // This happens during initial load before refreshGroupData completes
    if (selectedEquipmentObjects.length === 0 || selectedEquipmentObjects.length !== selectedEquipmentIds.length) {
      console.warn('Cannot detect group status - equipment not loaded yet. Waiting...');
      return;
    }

    const statuses = selectedEquipmentObjects.map(eq => eq.safety_equipment_status);
    console.log('Equipment statuses:', statuses);
    const assignedUsers = selectedEquipmentObjects.map(eq => eq.assigned_to).filter(user => user !== null);

    // Normalize issued/received to 'issued' for grouping purposes
    // These statuses are compatible and can be mixed in a group
    const normalizedStatuses = statuses.map(s => (s === 'received' ? 'issued' : s));
    const uniqueStatuses = [...new Set(normalizedStatuses)];

    if (uniqueStatuses.length === 1) {
      const commonStatus = uniqueStatuses[0];

      if (commonStatus === 'available') {
        // All equipment is available
        setFormData(prev => ({ ...prev, status: 'available' }));
        updateSmartFiltering('available');
      } else if (commonStatus === 'issued') {
        // All equipment is issued/received - set to the most common actual status
        const issuedCount = statuses.filter(s => s === 'issued').length;
        const receivedCount = statuses.filter(s => s === 'received').length;
        const actualStatus = issuedCount >= receivedCount ? 'issued' : 'received';

        setFormData(prev => ({ ...prev, status: actualStatus }));

        // Check if all issued/received equipment is assigned to the same user
        const uniqueUsers = [...new Set(assignedUsers)];
        if (uniqueUsers.length === 1) {
          setRequiredAssignedUser(uniqueUsers[0]);
        } else if (uniqueUsers.length > 1) {
          // Multiple users - this shouldn't happen in a group, but handle it
          console.warn('Equipment in group assigned to different users');
          setRequiredAssignedUser(null);
        }

        updateSmartFiltering(actualStatus);
      } else {
        // All equipment has the same status (service, damaged, out_of_date, etc.)
        // Use the actual status from the first item (since they're all the same)
        const actualStatus = statuses[0];
        console.log('Setting group status to:', actualStatus);
        setFormData(prev => ({ ...prev, status: actualStatus }));
        console.log('Calling updateSmartFiltering with:', actualStatus);
        updateSmartFiltering(actualStatus);
      }
    } else {
      // Mixed statuses - set to the most common one or available as default
      const availableCount = statuses.filter(s => s === 'available').length;
      const issuedReceivedCount = statuses.filter(s => s === 'issued' || s === 'received').length;

      if (availableCount > issuedReceivedCount) {
        setFormData(prev => ({ ...prev, status: 'available' }));
        updateSmartFiltering('available');
      } else {
        // Use issued as default for issued/received items
        const issuedCount = statuses.filter(s => s === 'issued').length;
        const receivedCount = statuses.filter(s => s === 'received').length;
        const actualStatus = issuedCount >= receivedCount ? 'issued' : 'received';
        setFormData(prev => ({ ...prev, status: actualStatus }));
        updateSmartFiltering(actualStatus);
      }
    }
  };

  // Get available equipment (not already assigned to other groups)
  const getAvailableEquipment = () => {
    console.log('🔍 getAvailableEquipment START');
    console.log('allEquipment count:', allEquipment.length);
    console.log('allEquipment IDs:', allEquipment.map(e => e.id));
    console.log('allEquipment statuses:', allEquipment.map(e => ({id: e.id, status: e.safety_equipment_status, group: e.equipment_group_id})));
    console.log('selectedEquipment:', selectedEquipment);
    console.log('smartFiltering:', smartFiltering);
    console.log('requiredStatus:', requiredStatus);
    console.log('requiredAssignedUser:', requiredAssignedUser);
    console.log('mode:', mode);
    console.log('groupData?.id:', groupData?.id);

    const result = allEquipment.filter(equipment => {
      const reasons = [];

      // Include equipment not in any group OR already selected for this group OR in THIS group (for view mode)
      const isInThisGroup = mode === 'view' && groupData && equipment.equipment_group_id === groupData.id;
      const isInNoGroup = !equipment.equipment_group_id;
      const isSelected = selectedEquipment.includes(equipment.id);
      const groupFilter = isInNoGroup || isSelected || isInThisGroup;

      if (!groupFilter) {
        reasons.push(`in different group (${equipment.equipment_group_id})`);
        console.log(`❌ Equipment ${equipment.id} filtered: ${reasons.join(', ')}`);
        return false;
      }

      // Filter to exclude deleted equipment (all other statuses are valid for grouping)
      if (equipment.safety_equipment_status === 'deleted') {
        reasons.push(`status deleted`);
        console.log(`❌ Equipment ${equipment.id} filtered: ${reasons.join(', ')}`);
        return false;
      }

      // Apply smart filtering if enabled
      // BUT: In view mode, items already in this group should ALWAYS pass (even if they don't match smart filtering)
      // This allows users to see all items in the group, even if there are data inconsistencies
      const isAlreadyInGroup = isInThisGroup || isSelected;

      if (smartFiltering && !isAlreadyInGroup) {
        // Filter by required status
        if (requiredStatus) {
          // Special handling for issued_or_received - accept both statuses
          if (requiredStatus === 'issued_or_received') {
            if (equipment.safety_equipment_status !== 'issued' && equipment.safety_equipment_status !== 'received') {
              reasons.push(`status mismatch (${equipment.safety_equipment_status} not in [issued, received])`);
              console.log(`❌ Equipment ${equipment.id} filtered: ${reasons.join(', ')}`);
              return false;
            }
          } else if (equipment.safety_equipment_status !== requiredStatus) {
            reasons.push(`status mismatch (${equipment.safety_equipment_status} != ${requiredStatus})`);
            console.log(`❌ Equipment ${equipment.id} filtered: ${reasons.join(', ')}`);
            return false;
          }
        }

        // For issued/received equipment, also filter by assigned user
        if (requiredStatus === 'issued_or_received' && requiredAssignedUser) {
          if (equipment.assigned_to !== requiredAssignedUser) {
            reasons.push(`assigned_to mismatch (${equipment.assigned_to} != ${requiredAssignedUser})`);
            console.log(`❌ Equipment ${equipment.id} filtered: ${reasons.join(', ')}`);
            return false;
          }
        }
      } else if (isAlreadyInGroup) {
        console.log(`✓ Equipment ${equipment.id} is already in group - bypassing smart filters`);
      }

      // Additional filtering based on status filters even when smart filtering is not active
      // This ensures status filters work independently
      // BUT: Items already in group should bypass these filters too
      if (selectedStatusFilter.length > 0 && !isAlreadyInGroup) {
        const statusMatch = selectedStatusFilter.includes(equipment.safety_equipment_status);
        if (!statusMatch) {
          reasons.push(`status filter mismatch (${equipment.safety_equipment_status} not in [${selectedStatusFilter.join(', ')}])`);
          console.log(`❌ Equipment ${equipment.id} filtered: ${reasons.join(', ')}`);
          return false;
        }

        // Special handling for issued/received status - if we have selected equipment with issued/received status,
        // limit to the same assigned user
        if ((equipment.safety_equipment_status === 'issued' || equipment.safety_equipment_status === 'received') && selectedEquipment.length > 0) {
          const selectedEquipmentObjects = allEquipment.filter(eq => selectedEquipment.includes(eq.id));
          const firstIssuedOrReceivedItem = selectedEquipmentObjects.find(eq =>
            eq.safety_equipment_status === 'issued' || eq.safety_equipment_status === 'received'
          );

          if (firstIssuedOrReceivedItem && equipment.assigned_to !== firstIssuedOrReceivedItem.assigned_to) {
            reasons.push(`issued/received assigned_to mismatch (${equipment.assigned_to} != ${firstIssuedOrReceivedItem.assigned_to})`);
            console.log(`❌ Equipment ${equipment.id} filtered: ${reasons.join(', ')}`);
            return false;
          }
        }
      }

      console.log(`✅ Equipment ${equipment.id} passed filters`);
      return true;
    });

    console.log('🔍 getAvailableEquipment END - filtered count:', result.length);
    console.log('🔍 Filtered equipment IDs:', result.map(e => e.id));
    return result;
  };

  // Filter and sort equipment
  const getSortedAndFilteredEquipment = () => {
    let filteredEquipment = getAvailableEquipment().filter(equipment => {
      // Don't show items that are already selected (they're shown in "Equipment in Group" list)
      if (selectedEquipment.includes(equipment.id)) {
        return false;
      }

      // Don't filter by search term here - backend already does it
      // We fetch new results from backend on every search change

      // Filter by equipment type
      const typeMatch = selectedTypeFilter.length === 0 ||
        selectedTypeFilter.includes(equipment.safety_equipment_type);

      // Filter by company
      const companyMatch = selectedCompanyFilter.length === 0 ||
        selectedCompanyFilter.includes(equipment.company);

      // Filter by model
      const modelMatch = selectedModelFilter.length === 0 ||
        selectedModelFilter.includes(equipment.safety_equipment_model);

      // Filter by status
      const statusMatch = selectedStatusFilter.length === 0 ||
        selectedStatusFilter.includes(equipment.safety_equipment_status);

      return typeMatch && companyMatch && modelMatch && statusMatch;
    });

    // Sort by type name, then by model name
    filteredEquipment.sort((a, b) => {
      const typeComparison = (a.safety_equipment_type_name || '').localeCompare(b.safety_equipment_type_name || '');
      if (typeComparison !== 0) return typeComparison;
      
      return getModelNameFromEquipment(a).localeCompare(getModelNameFromEquipment(b));
    });

    return filteredEquipment;
  };

  const getEquipmentDisplayName = (equipment) => {
    const type = equipment.safety_equipment_type_name || 'Unknown Type';
    const brand = getBrandFromEquipment(equipment);
    const model = getModelNameFromEquipment(equipment);
    const serial = equipment.safety_equipment_serial_number || 'No Serial';
    
    return `${type} - ${brand} ${model} (${serial})`;
  };

  // Handle equipment selection
  const handleEquipmentSelect = (equipment) => {
    const isSelected = selectedEquipment.includes(equipment.id);
    
    let newSelectedEquipment;
    if (isSelected) {
      // Remove equipment if already selected
      newSelectedEquipment = selectedEquipment.filter(id => id !== equipment.id);
      setSelectedEquipment(newSelectedEquipment);
    } else {
      // Add equipment
      newSelectedEquipment = [...selectedEquipment, equipment.id];
      setSelectedEquipment(newSelectedEquipment);
      
      // If this is the first equipment selected, enable smart filtering based on its status
      if (selectedEquipment.length === 0) {
        if (equipment.safety_equipment_status === 'available') {
          setSmartFiltering(true);
          setRequiredStatus('available');
          setRequiredAssignedUser(null);
        } else if (equipment.safety_equipment_status === 'issued' || equipment.safety_equipment_status === 'received') {
          setSmartFiltering(true);
          setRequiredStatus('issued_or_received');
          setRequiredAssignedUser(equipment.assigned_to);
        } else {
          setSmartFiltering(true);
          setRequiredStatus(equipment.safety_equipment_status);
          setRequiredAssignedUser(null);
        }
      } else {
        // If adding to existing selection, ensure consistency
        const selectedEquipmentObjects = allEquipment.filter(eq => selectedEquipment.includes(eq.id));
        const hasIssuedOrReceivedItems = selectedEquipmentObjects.some(eq =>
          eq.safety_equipment_status === 'issued' || eq.safety_equipment_status === 'received'
        );

        // If we already have issued/received items, and adding another issued/received item, ensure same user
        if (hasIssuedOrReceivedItems && (equipment.safety_equipment_status === 'issued' || equipment.safety_equipment_status === 'received')) {
          const firstIssuedOrReceivedItem = selectedEquipmentObjects.find(eq =>
            eq.safety_equipment_status === 'issued' || eq.safety_equipment_status === 'received'
          );
          if (firstIssuedOrReceivedItem && equipment.assigned_to !== firstIssuedOrReceivedItem.assigned_to) {
            // Don't allow selection - this case should be prevented by filtering
            console.warn('Attempting to select issued/received item assigned to different user');
            return;
          }
        }
      }
    }
    
    // Auto-detect and update group status based on selected equipment
    autoDetectGroupStatus(newSelectedEquipment);
  };

  // Remove equipment from selected list
  const handleRemoveEquipment = (equipmentId) => {
    const newSelectedEquipment = selectedEquipment.filter(id => id !== equipmentId);
    setSelectedEquipment(newSelectedEquipment);

    // Auto-detect and update group status based on remaining selected equipment
    autoDetectGroupStatus(newSelectedEquipment);
  };

  // Remove equipment from group (locally only - actual removal happens on Save Changes)
  const handleRemoveFromGroup = (equipmentId) => {
    console.log('handleRemoveFromGroup: Removing equipment ID', equipmentId, 'from local selection');

    // Simply remove from selectedEquipment array (no backend call, no confirmation)
    const newSelectedEquipment = selectedEquipment.filter(id => id !== equipmentId);
    setSelectedEquipment(newSelectedEquipment);

    console.log('selectedEquipment updated:', newSelectedEquipment);

    // Auto-detect and update group status based on remaining selected equipment
    if (newSelectedEquipment.length > 0) {
      autoDetectGroupStatus(newSelectedEquipment);
    }
  };

  // Delete entire group
  const handleDeleteGroup = async () => {
    if (!groupData || mode === 'create') {
      return;
    }

    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete the group "${groupData.name}"? This will NOT delete the equipment items, only the group itself.`)) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };

      // Delete the group
      await axios.delete(
        `${API_BASE_URL}/safetyequipmentgroup/${groupData.id}/`,
        config
      );

      toast.success("Group deleted successfully");

      // Refresh parent component
      if (onSave) {
        onSave();
      }

      // Close modal
      handleClose();
    } catch (error) {
      console.error("Error deleting group:", error);
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          const errorMessages = Object.entries(errorData)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('\n');
          toast.error(`Error: ${errorMessages}`);
        } else {
          toast.error(`Error: ${errorData}`);
        }
      } else {
        toast.error("Failed to delete group");
      }
    } finally {
      setLoading(false);
    }
  };

  // Get selected equipment display text
  const getSelectedEquipmentText = () => {
    if (selectedEquipment.length === 0) return 'Select equipment...';
    
    const selectedEquipmentObjects = allEquipment.filter(equipment => selectedEquipment.includes(equipment.id));
    if (selectedEquipmentObjects.length === 1) {
      return getEquipmentDisplayName(selectedEquipmentObjects[0]);
    }
    return `${selectedEquipmentObjects.length} equipment items selected`;
  };

  // Get status color classes
  const getStatusColorClasses = (status) => {
    switch (status) {
      case 'available':
        return 'text-green-600 font-bold bg-white hover:bg-gray-50';
      case 'issued':
        return 'text-purple-600 font-bold bg-white hover:bg-gray-50';
      case 'service':
        return 'text-orange-600 font-bold bg-white hover:bg-gray-50';
      case 'damaged':
        return 'text-red-600 font-bold bg-white hover:bg-gray-50';
      default:
        return 'text-gray-600 font-bold bg-white hover:bg-gray-50';
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Group name is required");
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      // Prepare data for API - don't include equipment IDs in group creation
      const submitData = {
        name: formData.name,
        description: formData.description,
        status: formData.status,
        location: formData.location,
        notes: formData.notes,
        nfc_tag_number: formData.nfc_tag_number.trim() || null,
        company: user?.company
      };

      console.log('Submitting group data:', submitData);
      console.log('Selected equipment IDs:', selectedEquipment);
      console.log('Pre-selected equipment:', preSelectedEquipment);

      let groupId = null;
      
      if (isEditing) {
        // Update existing group
        await axios.put(
          `${API_BASE_URL}/safetyequipmentgroup/${groupData.id}/`,
          submitData,
          config
        );
        groupId = groupData.id;
        toast.success("Group updated successfully");
      } else if (mode === 'view') {
        // In view mode, we're only adding equipment to existing group - no group data update needed
        groupId = groupData.id;
      } else {
        // Create new group
        const response = await axios.post(
          `${API_BASE_URL}/safetyequipmentgroup/`,
          submitData,
          config
        );
        groupId = response.data.id;
        console.log('Created group with ID:', groupId);
        toast.success("Group created successfully");
      }

      // Handle equipment changes (add/remove) for the group
      if (groupId) {
        const { added, removed } = equipmentChanges;
        const promises = [];

        // Add newly selected equipment to the group
        if (added.length > 0) {
          console.log('Adding equipment to group:', added);
          const addPromises = added.map(equipmentId =>
            axios.patch(
              `${API_BASE_URL}/safetyequipment/${equipmentId}/`,
              { equipment_group: groupId },
              config
            ).catch(error => {
              console.error(`Failed to add equipment ${equipmentId} to group:`, error);
              return { success: false, id: equipmentId, action: 'add' };
            }).then(result => result?.success === false ? result : { success: true, id: equipmentId, action: 'add' })
          );
          promises.push(...addPromises);
        }

        // Remove deselected equipment from the group
        if (removed.length > 0) {
          console.log('Removing equipment from group:', removed);
          const removePromises = removed.map(equipmentId =>
            axios.patch(
              `${API_BASE_URL}/safetyequipment/${equipmentId}/`,
              { equipment_group: null },
              config
            ).catch(error => {
              console.error(`Failed to remove equipment ${equipmentId} from group:`, error);
              return { success: false, id: equipmentId, action: 'remove' };
            }).then(result => result?.success === false ? result : { success: true, id: equipmentId, action: 'remove' })
          );
          promises.push(...removePromises);
        }

        // Execute all add/remove operations
        if (promises.length > 0) {
          try {
            const results = await Promise.all(promises);
            const addedSuccess = results.filter(r => r.action === 'add' && r.success).length;
            const removedSuccess = results.filter(r => r.action === 'remove' && r.success).length;
            const addedFailed = results.filter(r => r.action === 'add' && !r.success).length;
            const removedFailed = results.filter(r => r.action === 'remove' && !r.success).length;

            console.log(`Equipment changes: +${addedSuccess} added, -${removedSuccess} removed`);

            // Show appropriate success/warning message
            if (addedFailed === 0 && removedFailed === 0) {
              // All operations successful
              if (addedSuccess > 0 && removedSuccess > 0) {
                toast.success(`Changes saved: ${addedSuccess} item${addedSuccess > 1 ? 's' : ''} added, ${removedSuccess} item${removedSuccess > 1 ? 's' : ''} removed`);
              } else if (addedSuccess > 0) {
                toast.success(`Successfully added ${addedSuccess} item${addedSuccess > 1 ? 's' : ''}`);
              } else if (removedSuccess > 0) {
                toast.success(`Successfully removed ${removedSuccess} item${removedSuccess > 1 ? 's' : ''}`);
              }
            } else {
              // Some operations failed
              toast.warning(`Partial success: ${addedSuccess}/${added.length} added, ${removedSuccess}/${removed.length} removed`);
            }

            // In view mode, fetch fresh data from backend after changes
            if (mode === 'view') {
              console.log('Refreshing group data after save...');
              // Refresh group data (includes fresh equipment data for all items in group)
              await refreshGroupData();

              console.log('Refreshing available equipment list...');
              // Also refresh the available equipment list to ensure all data is current
              await fetchEquipment();

              // Mark that we need to refresh parent when modal closes
              needsParentRefreshRef.current = true;

              console.log('Data refresh completed');
            }
          } catch (error) {
            console.error('Error updating equipment assignments:', error);
            toast.error('Failed to save some changes');
          }
        } else if (mode !== 'view') {
          // In create/edit mode with equipment, assign all selected equipment
          if (selectedEquipment.length > 0) {
            const assignPromises = selectedEquipment.map(equipmentId =>
              axios.patch(
                `${API_BASE_URL}/safetyequipment/${equipmentId}/`,
                { equipment_group: groupId },
                config
              ).catch(error => {
                console.error(`Failed to assign equipment ${equipmentId}:`, error);
                return null;
              })
            );

            const results = await Promise.all(assignPromises);
            const successCount = results.filter(r => r !== null).length;
            if (successCount < selectedEquipment.length) {
              toast.warning(`Group saved but only ${successCount}/${selectedEquipment.length} equipment items were assigned`);
            }
          }
        }
      }

      // In create/edit mode, notify parent and close modal
      if (mode !== 'view') {
        if (onSave) {
          onSave();
        }
        handleClose();
      }
      // In view mode, DON'T notify parent yet - it will be called when modal closes
    } catch (error) {
      console.error("Error saving group:", error);
      console.error("Response data:", error.response?.data);
      console.error("Error status:", error.response?.status);
      console.error("Error headers:", error.response?.headers);
      
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          // Check if there's a single 'error' field - display it directly
          if (errorData.error && Object.keys(errorData).length === 1) {
            toast.error(errorData.error);
          } else {
            // Multiple fields - format them all
            const errorMessages = Object.entries(errorData)
              .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
              .join('\n');
            toast.error(`Error: ${errorMessages}`);
          }
        } else {
          toast.error(`Error: ${errorData}`);
        }
      } else {
        toast.error("Failed to save group");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle group inspection
  const handleInspectGroup = async () => {
    // Use current selectedEquipment state instead of groupData.equipment_items
    if (!selectedEquipment || selectedEquipment.length === 0) {
      toast.warning("No equipment items in this group to inspect");
      return;
    }

    if (onInspectGroup) {
      try {
        setLoading(true);
        // Fetch full equipment details for inspection modal (same as assignment)
        const token = localStorage.getItem("access_token");

        // Use current selectedEquipment state (updated after add/remove operations)
        const equipmentIds = selectedEquipment;

        console.log('handleInspectGroup: Using selectedEquipment IDs:', equipmentIds);

        // Fetch full equipment data for all items currently in the group
        const equipmentPromises = equipmentIds.map(id =>
          axios.get(`${API_BASE_URL}/safetyequipment/${id}/`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        );

        const equipmentResponses = await Promise.all(equipmentPromises);
        const fullEquipmentData = equipmentResponses.map(response => response.data);

        console.log('handleInspectGroup: Fetched full data for', fullEquipmentData.length, 'items');

        // Convert groupData format to match expected format with full equipment data
        const groupForInspection = {
          ...(localGroupData || groupData), // Use localGroupData if available (it's more up-to-date)
          groupItems: fullEquipmentData // Use full equipment data instead of simplified
        };

        handleClose();
        onInspectGroup(groupForInspection);
      } catch (error) {
        console.error("Error fetching equipment details:", error);
        toast.error("Failed to load equipment details for inspection");
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle group assignment
  const handleAssignGroup = async () => {
    // Use current selectedEquipment state instead of groupData.equipment_items
    // This ensures we work with the most up-to-date list after any additions/removals
    if (!selectedEquipment || selectedEquipment.length === 0) {
      toast.warning("No equipment items in this group to assign");
      return;
    }

    if (onAssignGroup) {
      try {
        // Fetch full equipment details for assignment modal
        const token = localStorage.getItem("access_token");

        // Use current selectedEquipment state (updated after add/remove operations)
        const equipmentIds = selectedEquipment;

        console.log('handleAssignGroup: Using selectedEquipment IDs:', equipmentIds);

        // Fetch full equipment data for all items currently in the group
        const equipmentPromises = equipmentIds.map(id =>
          axios.get(`${API_BASE_URL}/safetyequipment/${id}/`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        );

        const equipmentResponses = await Promise.all(equipmentPromises);
        const fullEquipmentData = equipmentResponses.map(response => response.data);

        console.log('handleAssignGroup: Fetched full data for', fullEquipmentData.length, 'items');

        // Convert groupData format to match expected format with full equipment data
        const groupForAssignment = {
          ...(localGroupData || groupData), // Use localGroupData if available (it's more up-to-date)
          groupItems: fullEquipmentData // Use full equipment data instead of simplified
        };

        handleClose();
        onAssignGroup(groupForAssignment);
      } catch (error) {
        console.error("Error fetching equipment details:", error);
        toast.error("Failed to load equipment details for assignment");
      }
    }
  };

  // Handle return from user - end all user assignments for equipment in the group
  const handleReturnFromUser = async () => {
    if (!selectedEquipment || selectedEquipment.length === 0) {
      toast.warning("No equipment items in this group");
      return;
    }

    // Get items with user assignments
    const currentEquipmentItems = allEquipment.filter(eq => selectedEquipment.includes(eq.id));
    const itemsWithUserAssignments = currentEquipmentItems.filter(item => item.assignment?.user_id);

    if (itemsWithUserAssignments.length === 0) {
      toast.warning("No equipment assigned to users in this group");
      return;
    }

    console.log('handleReturnFromUser: Items with user assignments:', itemsWithUserAssignments);

    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      // For each item with user assignment, find and update its active user assignment
      const returnPromises = itemsWithUserAssignments.map(async (item) => {
        try {
          // Find active user assignments for this equipment
          const assignmentsResponse = await axios.get(
            `${API_BASE_URL}/equipmentassignment/?safety_equipment=${item.id}&status=assigned`,
            config
          );

          const activeAssignments = assignmentsResponse.data.results || assignmentsResponse.data || [];

          // Update each active assignment to returned
          const assignmentPromises = activeAssignments.map(assignment =>
            axios.patch(
              `${API_BASE_URL}/equipmentassignment/${assignment.id}/`,
              {
                status: 'returned',
                return_date: new Date().toISOString()
              },
              config
            )
          );

          await Promise.all(assignmentPromises);

          // Update equipment status to available only if no project assignment
          if (!item.assignment?.project_name) {
            await axios.patch(
              `${API_BASE_URL}/safetyequipment/${item.id}/`,
              { safety_equipment_status: 'available' },
              config
            );
          }

          return { success: true, itemId: item.id };
        } catch (error) {
          console.error(`Error returning equipment ${item.id} from user:`, error);
          return { success: false, itemId: item.id, error };
        }
      });

      const results = await Promise.all(returnPromises);
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);

      if (successful.length > 0) {
        toast.success(`Successfully returned ${successful.length} equipment item${successful.length > 1 ? 's' : ''} from user`);

        // Refresh data
        await refreshGroupData();
        await fetchEquipment();
      }

      if (failed.length > 0) {
        toast.error(`Failed to return ${failed.length} equipment item${failed.length > 1 ? 's' : ''} from user`);
      }

      // Refresh parent component data if there's a callback
      if (onSave) {
        onSave();
      }

    } catch (error) {
      console.error("Error returning equipment from user:", error);
      toast.error("Failed to return equipment from user");
    } finally {
      setLoading(false);
    }
  };

  // Handle return from project - end all project assignments for equipment in the group
  const handleReturnFromProject = async () => {
    if (!selectedEquipment || selectedEquipment.length === 0) {
      toast.warning("No equipment items in this group");
      return;
    }

    // Get items with project assignments
    const currentEquipmentItems = allEquipment.filter(eq => selectedEquipment.includes(eq.id));
    const itemsWithProjectAssignments = currentEquipmentItems.filter(item => item.assignment?.project_name);

    if (itemsWithProjectAssignments.length === 0) {
      toast.warning("No equipment assigned to projects in this group");
      return;
    }

    console.log('handleReturnFromProject: Items with project assignments:', itemsWithProjectAssignments);

    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      // For each item with project assignment, find and update its active project assignment
      const returnPromises = itemsWithProjectAssignments.map(async (item) => {
        try {
          // Find active project assignments for this equipment
          const assignmentsResponse = await axios.get(
            `${API_BASE_URL}/projectassignment/?safety_equipment=${item.id}&status=assigned`,
            config
          );

          const activeAssignments = assignmentsResponse.data.results || assignmentsResponse.data || [];

          // Update each active project assignment to returned
          const assignmentPromises = activeAssignments.map(assignment =>
            axios.patch(
              `${API_BASE_URL}/projectassignment/${assignment.id}/`,
              {
                status: 'returned',
                return_date: new Date().toISOString()
              },
              config
            )
          );

          await Promise.all(assignmentPromises);

          // Update equipment status to available only if no user assignment
          if (!item.assignment?.user_id) {
            await axios.patch(
              `${API_BASE_URL}/safetyequipment/${item.id}/`,
              { safety_equipment_status: 'available' },
              config
            );
          }

          return { success: true, itemId: item.id };
        } catch (error) {
          console.error(`Error returning equipment ${item.id} from project:`, error);
          return { success: false, itemId: item.id, error };
        }
      });

      const results = await Promise.all(returnPromises);
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);

      if (successful.length > 0) {
        toast.success(`Successfully returned ${successful.length} equipment item${successful.length > 1 ? 's' : ''} from project`);

        // Refresh data
        await refreshGroupData();
        await fetchEquipment();
      }

      if (failed.length > 0) {
        toast.error(`Failed to return ${failed.length} equipment item${failed.length > 1 ? 's' : ''} from project`);
      }

      // Refresh parent component data if there's a callback
      if (onSave) {
        onSave();
      }

    } catch (error) {
      console.error("Error returning equipment from project:", error);
      toast.error("Failed to return equipment from project");
    } finally {
      setLoading(false);
    }
  };

  // Analyze assignments in the group
  const analyzeAssignments = () => {
    const currentEquipmentItems = allEquipment.filter(eq => selectedEquipment.includes(eq.id));

    const assignmentMap = {
      hasUserAssignments: false,
      hasProjectAssignments: false,
      userAssignments: [],
      projectAssignments: [],
      users: new Set(),
      projects: new Set()
    };

    currentEquipmentItems.forEach(item => {
      if (item.assignment) {
        // Check for user assignment
        if (item.assignment.user_id) {
          assignmentMap.hasUserAssignments = true;
          assignmentMap.userAssignments.push(item);
          assignmentMap.users.add(`${item.assignment.user_name} (ID: ${item.assignment.user_id})`);
        }

        // Check for project assignment
        if (item.assignment.project_name) {
          assignmentMap.hasProjectAssignments = true;
          assignmentMap.projectAssignments.push(item);
          assignmentMap.projects.add(item.assignment.project_name);
        }
      }
    });

    return assignmentMap;
  };

  const assignmentAnalysis = analyzeAssignments();

  // Check if user has inspection permissions and group is not out of date
  const canInspect = user?.inspector === true && groupData?.status !== 'out_of_date';

  // Check if group can be assigned (has available equipment items without assignments)
  // Use current selectedEquipment state instead of groupData.equipment_items
  const canAssign = selectedEquipment.length > 0 && allEquipment
    .filter(eq => selectedEquipment.includes(eq.id))
    .some(item =>
      (item.safety_equipment_status === 'available' || item.status === 'available') &&
      !item.assignment?.user_id && !item.assignment?.project_name
    );

  // Check if group can be returned from user
  const canReturnFromUser = assignmentAnalysis.hasUserAssignments;

  // Check if group can be returned from project
  const canReturnFromProject = assignmentAnalysis.hasProjectAssignments;

  // Calculate equipment changes (added/removed) compared to original
  const getEquipmentChanges = () => {
    const added = selectedEquipment.filter(id => !originalEquipmentIds.includes(id));
    const removed = originalEquipmentIds.filter(id => !selectedEquipment.includes(id));
    const hasChanges = added.length > 0 || removed.length > 0;

    return { added, removed, hasChanges };
  };

  const equipmentChanges = getEquipmentChanges();

  // Determine button text based on changes
  const getSaveButtonText = () => {
    const { added, removed } = equipmentChanges;

    if (added.length > 0 && removed.length > 0) {
      return 'Save Changes';
    } else if (added.length > 0) {
      return added.length === 1 ? 'Add Item' : `Add ${added.length} Items`;
    } else if (removed.length > 0) {
      return removed.length === 1 ? 'Remove Item' : `Remove ${removed.length} Items`;
    }

    return 'Save Changes';
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEquipmentDropdown && !event.target.closest('.equipment-dropdown-container')) {
        setShowEquipmentDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEquipmentDropdown]);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className={`${showEquipmentPanel && showFiltersPanel ? 'sm:max-w-[1800px]' : showEquipmentPanel ? 'sm:max-w-[1200px]' : 'sm:max-w-[600px]'} h-[95vh] max-h-[95vh] flex flex-col`}>
        <DialogHeader>
          <div className={`${showEquipmentPanel ? 'flex gap-6 items-center' : ''}`}>
            <div className={`${showEquipmentPanel ? 'flex-1' : ''}`}>
              <DialogTitle className="flex items-center space-x-2">
                <Users className="h-5 w-5 text-blue-600" />
                <span>
                  {mode === 'create' && 'Create Equipment Group'}
                  {mode === 'edit' && 'Edit Equipment Group'}
                  {mode === 'view' && 'View Equipment Group'}
                </span>
              </DialogTitle>
            </div>
            {showEquipmentPanel && (
              <div className="flex-1">
                <h3 className="text-lg font-medium">Manage Equipment</h3>
              </div>
            )}
            {showFiltersPanel && (
              <div className="flex-1">
                <h3 className="text-lg font-medium">Filters</h3>
              </div>
            )}
          </div>
        </DialogHeader>
        
        <div className={`${showEquipmentPanel ? 'flex gap-6' : ''} flex-1 overflow-hidden`}>
          {/* Main Group Details Form */}
          <div className={`${showEquipmentPanel ? 'flex-1' : ''} space-y-4 overflow-y-auto pr-2`}>
            {/* Group Name and Status */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Group Name *</label>
                <input
                  type="text"
                  placeholder="Enter group name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  disabled={mode === 'view'}
                  className="w-full p-2 border rounded disabled:bg-gray-100"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange("status", e.target.value)}
                  disabled={mode === 'view' || (preSelectedEquipment && preSelectedEquipment.length > 0) || (smartFiltering && selectedEquipment.length > 0)}
                  className="w-full p-2 border rounded disabled:bg-gray-100"
                >
                  <option value="">Choose status</option>
                  <option value="available">Available</option>
                  <option value="issued">Issued</option>
                  <option value="received">Received</option>
                  <option value="service">Service</option>
                  <option value="damaged">Damaged</option>
                  <option value="out_of_date">Out of date</option>
                </select>
                {smartFiltering && selectedEquipment.length > 0 && (
                  <div className="text-xs text-blue-600">
                    Status automatically set based on selected equipment
                  </div>
                )}
              </div>
            </div>

            {/* Location and NFC Tag */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Location</label>
                <input
                  type="text"
                  placeholder="Enter location"
                  value={formData.location}
                  onChange={(e) => handleInputChange("location", e.target.value)}
                  disabled={mode === 'view'}
                  className="w-full p-2 border rounded disabled:bg-gray-100"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">NFC Tag Number</label>
                <input
                  type="text"
                  placeholder="Enter NFC tag number"
                  value={formData.nfc_tag_number}
                  onChange={(e) => handleInputChange("nfc_tag_number", e.target.value)}
                  disabled={mode === 'view'}
                  className="w-full p-2 border rounded disabled:bg-gray-100"
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <textarea
                placeholder="Enter description"
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                disabled={mode === 'view'}
                className="w-full p-2 border rounded h-20 resize-none disabled:bg-gray-100"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Notes</label>
              <textarea
                placeholder="Additional notes"
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                disabled={mode === 'view'}
                className="w-full p-2 border rounded h-20 resize-none disabled:bg-gray-100"
              />
            </div>
          </div>

          {/* Equipment Management Panel */}
          {showEquipmentPanel && (
            <div className="flex-1 border-l border-gray-200 pl-6 overflow-y-auto">
              <div className="space-y-3 h-full">
                {/* Info and Filters Button */}
                <div className="flex justify-between items-center gap-4">
                  <div className="text-sm text-gray-600">
                    Selected: {selectedEquipment.length} equipment items
                    {smartFiltering && (
                      <div className="text-xs text-blue-600 mt-1">
                        Smart filtering: Only {requiredStatus === 'issued_or_received' ? 'issued/received' : requiredStatus} equipment
                        {requiredStatus === 'issued_or_received' && requiredAssignedUser && (
                          ` assigned to ${getUserNameById(requiredAssignedUser)}`
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowFiltersPanel(!showFiltersPanel)}
                      className="text-xs px-3 py-1 h-8"
                    >
                      Filters {showFiltersPanel ? '▼' : '▲'}
                    </Button>
                  </div>
                </div>
                
                {/* Equipment Multiselect - Available in all modes */}
                {(
                  <div className="space-y-2 relative equipment-dropdown-container">
                    <label className="text-sm font-medium">Select Equipment</label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search equipment... (1 sec delay or press Enter)"
                        value={equipmentFilter}
                        onChange={(e) => setEquipmentFilter(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            // Cancel debounce and fetch immediately
                            fetchEquipment(equipmentFilter);
                          }
                        }}
                        onFocus={() => setShowEquipmentDropdown(true)}
                        className="w-full p-2 border rounded"
                      />
                      {hasMoreEquipment && equipmentFilter.length === 0 && (
                        <div className="text-xs text-orange-600 mt-1">
                          ⚠️ Showing first 100 items. Use search to find specific equipment.
                        </div>
                      )}
                    </div>

                  {showEquipmentDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-[480px] overflow-y-auto">
                      {getSortedAndFilteredEquipment().length > 0 ? (
                        getSortedAndFilteredEquipment().map((equipment) => {
                          const isSelected = selectedEquipment.includes(equipment.id);

                          return (
                            <div
                              key={equipment.id}
                              onClick={() => handleEquipmentSelect(equipment)}
                              className={`p-3 cursor-pointer border-b last:border-b-0 ${
                                getStatusColorClasses(equipment.safety_equipment_status)
                              } ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <div className="font-medium">{getEquipmentDisplayName(equipment)}</div>
                                  <div className="text-xs flex gap-2">
                                    <span className="text-gray-600">
                                      {equipment.company_name || 'No company'}
                                    </span>
                                    <span className="text-gray-400">•</span>
                                    <span>
                                      Status: {equipment.safety_equipment_status}
                                    </span>
                                    {(equipment.safety_equipment_status === 'issued' || equipment.safety_equipment_status === 'received') && equipment.assigned_to_name && (
                                      <>
                                        <span className="text-gray-400">•</span>
                                        <span>
                                          Assigned to: {equipment.assigned_to_name}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <div className="text-xs">
                                  {isSelected && '✓'}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="p-3 text-gray-500 text-center">
                          {equipmentFilter.length > 0 ? 'No equipment found matching your search' : 'No equipment available'}
                        </div>
                      )}
                    </div>
                  )}
                  </div>
                )}

                {/* Selected Equipment List */}
                {selectedEquipment.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">
                      {mode === 'view' ? 'Equipment in Group' : 'Selected Equipment'} ({selectedEquipment.length})
                    </h4>
                    <div className="space-y-2 max-h-[552px] overflow-y-auto">
                      {selectedEquipment.map((equipmentId) => {
                        const equipment = allEquipment.find(e => e.id === equipmentId);
                        if (!equipment) return null;
                        
                        return (
                          <div
                            key={equipmentId}
                            className={`p-2 rounded border ${getStatusColorClasses(equipment.safety_equipment_status)} flex justify-between items-center`}
                          >
                            <div className="flex-1">
                              <div className="font-medium text-sm">{getEquipmentDisplayName(equipment)}</div>
                              <div className="text-xs flex gap-2">
                                <span className="text-gray-600">
                                  {equipment.company_name || 'No company'}
                                </span>
                                <span className="text-gray-400">•</span>
                                <span>
                                  Status: {equipment.safety_equipment_status}
                                </span>
                                {(equipment.safety_equipment_status === 'issued' || equipment.safety_equipment_status === 'received') && equipment.assigned_to_name && (
                                  <>
                                    <span className="text-gray-400">•</span>
                                    <span>
                                      Assigned to: {equipment.assigned_to_name}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveFromGroup(equipmentId)}
                              className="text-red-500 hover:text-red-700 font-bold text-sm ml-2 p-1"
                              title={mode === 'view' ? "Remove from group" : "Remove equipment"}
                              disabled={loading}
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Filters Panel */}
          {showFiltersPanel && (
            <div className="flex-1 border-l border-gray-200 pl-6 overflow-y-auto">
              <div className="space-y-4 h-full">
                {/* Clear All Filters */}
                <div className="mb-6">
                  <button
                    onClick={() => {
                      setSelectedTypeFilter([]);
                      setSelectedCompanyFilter([]);
                      setSelectedModelFilter([]);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Clear All Filters
                  </button>
                </div>

                {/* Filters Grid */}
                <div className="grid grid-cols-1 gap-4 flex-1 flex-wrap">
                  {/* Equipment Types Filter */}
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Equipment Types</label>
                    <MultiSelectDropdown
                      options={equipmentTypes.map(type => ({ value: type.id, label: type.name }))}
                      value={selectedTypeFilter}
                      onChange={setSelectedTypeFilter}
                      placeholder="Select equipment types..."
                      searchPlaceholder="Search types..."
                    />
                  </div>

                  {/* Models Filter */}
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Models</label>
                    <MultiSelectDropdown
                      options={models.map(model => ({ value: model.id, label: model.name }))}
                      value={selectedModelFilter}
                      onChange={setSelectedModelFilter}
                      placeholder="Select models..."
                      searchPlaceholder="Search models..."
                    />
                  </div>

                  {/* Companies Filter */}
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Companies</label>
                    <MultiSelectDropdown
                      options={companies.map(company => ({ value: company.id, label: company.name }))}
                      value={selectedCompanyFilter}
                      onChange={setSelectedCompanyFilter}
                      placeholder="Select companies..."
                      searchPlaceholder="Search companies..."
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between flex-shrink-0 mt-4 border-t pt-4">
          <div className="flex space-x-2">
            {/* Delete Group Button - in view and edit mode, hidden when there are unsaved changes */}
            {(mode === 'view' || mode === 'edit') && groupData && !equipmentChanges.hasChanges && (
              <Button
                variant="outline"
                onClick={handleDeleteGroup}
                disabled={loading}
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                🗑️ Delete Group
              </Button>
            )}

            {/* Group Action Buttons - in view and edit mode, hidden when there are unsaved changes */}
            {(mode === 'view' || mode === 'edit') && groupData && groupData.equipment_items && groupData.equipment_items.length > 0 && !equipmentChanges.hasChanges && (
              <>
                {/* Inspection button - only for inspectors */}
                {canInspect && onInspectGroup && (
                  <Button
                    variant="outline"
                    onClick={handleInspectGroup}
                    disabled={loading}
                    className="text-purple-600 border-purple-300 hover:bg-purple-50"
                  >
                    🔍 Inspection
                  </Button>
                )}

                {/* Assignment button - only when group has available equipment without assignments */}
                {(canAssign && onAssignGroup) && (
                  <Button
                    variant="outline"
                    onClick={handleAssignGroup}
                    disabled={loading}
                    className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                  >
                    👤 Assignment
                  </Button>
                )}

                {/* Return from User button - only when group has equipment assigned to users */}
                {canReturnFromUser && (
                  <Button
                    variant="outline"
                    onClick={handleReturnFromUser}
                    disabled={loading}
                    className="text-green-600 border-green-300 hover:bg-green-50"
                  >
                    ↩️ Return from User
                  </Button>
                )}

                {/* Return from Project button - only when group has equipment assigned to projects */}
                {canReturnFromProject && (
                  <Button
                    variant="outline"
                    onClick={handleReturnFromProject}
                    disabled={loading}
                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                  >
                    📦 Return from Project
                  </Button>
                )}
              </>
            )}
            
            {/* Save Changes button - show when there are equipment changes in view mode */}
            {mode === 'view' && equipmentChanges.hasChanges && (
              <Button
                onClick={handleSave}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {getSaveButtonText()}
              </Button>
            )}
          </div>
          <div className="space-x-2">
            <Button variant="outline" onClick={handleClose} disabled={loading}>
              {mode === 'view' ? 'Close' : 'Cancel'}
            </Button>
            {/* Show Add Equipment button - hidden in view mode when there are unsaved changes */}
            {(mode === 'view' ? !equipmentChanges.hasChanges : true) && (mode === 'view' || (mode !== 'view' && (!showEquipmentPanel || selectedEquipment.length === 0))) && (
              <Button
                variant="secondary"
                onClick={() => {
                  if (!showEquipmentPanel) {
                    fetchEquipment();
                  } else {
                    // When hiding equipment panel, also hide filters panel
                    setShowFiltersPanel(false);
                  }
                  setShowEquipmentPanel(!showEquipmentPanel);
                }}
                disabled={loading}
              >
{showEquipmentPanel ? "Hide Equipment" : (mode === 'view' ? "Select Equipment" : "Add Equipment")}
              </Button>
            )}
            {mode !== 'view' && (
              <Button onClick={handleSave} disabled={loading || !formData.name.trim()}>
                {loading ? "Saving..." : isEditing ? "Update" : "Create"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GroupModal;